import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isProjectOwnerOrMember, calculateProjectSkillMatch } from '../utils/userPermissions';
import { 
  ArrowLeft, 
  Users, 
  Target, 
  TrendingUp, 
  Plus, 
  Sparkles, 
  CheckCircle, 
  CheckCircle2, 
  Clock, 
  Send, 
  X, 
  AlertCircle, 
  UserCheck, 
  UserX, 
  Edit3
} from 'lucide-react';

const PROJECT_CATEGORIES = [
  'General',
  'Software & Web Development',
  'AI & Machine Learning',
  'Data Engineering & Analytics',
  'Cloud & DevOps',
  'Mobile Applications',
  'Product Design & UX',
  'Business & Operations'
];

const ProjectDetails = () => {
  const { id } = useParams();
  const { isDarkMode } = useTheme();
  const { currentUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  // Load live projects & applications
  const [projects, setProjects] = useState(() => JSON.parse(localStorage.getItem('skillmesh_projects') || '[]'));
  const [applications, setApplications] = useState(() => JSON.parse(localStorage.getItem('skillmesh_applications') || '[]'));

  // Apply Modal state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [pitchMessage, setPitchMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Edit Project Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    category: 'General',
    status: 'Planning',
    teamSize: 4,
    startDate: '',
    endDate: '',
    estimatedHours: '',
    progress: 0,
    skills: []
  });
  const [newEditSkill, setNewEditSkill] = useState({
    name: '',
    level: 'Intermediate',
    importance: 'High'
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const loadData = async () => {
    try {
      const [projData, appsData] = await Promise.all([
        api.getProjects(),
        api.getApplications()
      ]);
      if (Array.isArray(projData)) setProjects(projData);
      if (Array.isArray(appsData)) setApplications(appsData);
    } catch (e) {
      console.warn('Error loading project details data:', e);
    }
  };

  useEffect(() => {
    loadData();

    const handleStorageChange = () => {
      const savedProjects = localStorage.getItem('skillmesh_projects');
      if (savedProjects) setProjects(JSON.parse(savedProjects));
      const savedApps = localStorage.getItem('skillmesh_applications');
      if (savedApps) setApplications(JSON.parse(savedApps));
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [id]);

  const rawProject = projects.find(p => String(p.id) === String(id) || p.name === id);

  if (!rawProject) {
    return (
      <div className="card text-center py-16 max-w-lg mx-auto">
        <Target className="h-12 w-12 text-gray-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Project Not Found</h2>
        <p className="text-gray-400 mb-6">The requested project could not be found.</p>
        <Link to="/projects" className="btn-primary">
          Back to Projects
        </Link>
      </div>
    );
  }

  // Check relationship of current user to this project
  const isOwner = (rawProject.creatorEmail && rawProject.creatorEmail.toLowerCase() === currentUser?.email?.toLowerCase()) ||
                  (rawProject.creator && rawProject.creator.toLowerCase() === currentUser?.name?.toLowerCase()) ||
                  isAdmin;

  const isMember = isProjectOwnerOrMember(rawProject, currentUser);

  // Check if current user has an application
  const userApplication = applications.find(a => 
    (String(a.projectId) === String(rawProject.id) || a.projectName === rawProject.name) &&
    (a.userEmail || '').toLowerCase() === (currentUser?.email || '').toLowerCase()
  );

  // Applications for this project (visible to owner/admin)
  const projectApplications = applications.filter(a => 
    String(a.projectId) === String(rawProject.id) || a.projectName === rawProject.name
  );

  // Normalize project structure for display
  const skillsList = Array.isArray(rawProject.requiredSkills)
    ? rawProject.requiredSkills.map(s => typeof s === 'string' ? { name: s, level: 'Intermediate', importance: 'High' } : s)
    : [];

  const rawMembers = Array.isArray(rawProject.teamMembers) ? rawProject.teamMembers : [];
  const currentTeamCount = rawMembers.length > 0 
    ? rawMembers.length 
    : (typeof rawProject.currentTeam === 'number' ? rawProject.currentTeam : 1);

  const teamSize = rawProject.teamSize || 4;
  const openSpots = Math.max(0, teamSize - currentTeamCount);

  const members = rawMembers.length > 0
    ? rawMembers.map((m, idx) => {
        const name = typeof m === 'string' ? m : (m?.name || 'Collaborator');
        const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
        return {
          id: idx,
          name,
          avatar: initials,
          role: idx === 0 ? 'Project Lead' : 'Team Member'
        };
      })
    : [
        {
          id: 0,
          name: rawProject.creator || 'Project Owner',
          avatar: (rawProject.creator || 'PO').slice(0, 2).toUpperCase(),
          role: 'Project Lead'
        }
      ];

  const project = {
    ...rawProject,
    progress: typeof rawProject.progress === 'number' ? rawProject.progress : (rawProject.status === 'Completed' ? 100 : 0),
    teamSize,
    currentTeamCount,
    skillsList,
    members
  };

  // Calculate skill fit for current user
  const skillMatch = calculateProjectSkillMatch(project.requiredSkills, currentUser?.skills);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      case 'In Progress':
        return isDarkMode ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-50 text-blue-800 border border-blue-300 font-semibold';
      case 'Planning':
        return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-semibold';
      default:
        return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'Critical':
        return isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-300 font-semibold';
      case 'High':
        return isDarkMode ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-orange-50 text-orange-800 border border-orange-300 font-semibold';
      case 'Medium':
        return isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold';
      case 'Low':
        return isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-green-50 text-green-800 border border-green-300 font-semibold';
      default:
        return isDarkMode ? 'bg-dark-700 text-dark-300' : 'bg-light-200 text-light-700';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'skills', label: `Required Skills (${skillsList.length})`, icon: Target },
    { id: 'team', label: `Team (${currentTeamCount}/${teamSize})`, icon: Users },
    ...(isOwner ? [
      { id: 'applicants', label: `Applicants (${projectApplications.length})`, icon: UserCheck, highlight: projectApplications.length > 0 }
    ] : [])
  ];

  const handleOpenApplyModal = () => {
    setPitchMessage(`Hello! I'm eager to join the "${project.name}" team. My skills align with your requirements, and I would love to contribute my experience.`);
    setIsApplyModalOpen(true);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      const userSkillNames = (currentUser.skills || []).map(s => typeof s === 'string' ? s : s.name);
      const newApp = await api.applyToProject({
        projectId: project.id,
        projectName: project.name,
        userEmail: currentUser.email,
        userName: currentUser.name || currentUser.email.split('@')[0],
        message: pitchMessage,
        skills: userSkillNames
      });

      setApplications(prev => [newApp, ...prev.filter(a => a.id !== newApp.id)]);
      setIsApplyModalOpen(false);
      setFeedback({
        type: 'success',
        text: 'Your application was submitted successfully! The project lead has been notified.'
      });
      setTimeout(() => setFeedback(null), 5000);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: 'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateApplicantStatus = async (applicantEmail, newStatus) => {
    try {
      await api.updateApplicationStatus(project.id, applicantEmail, newStatus);
      // Reload applications and projects
      await loadData();
      setFeedback({
        type: 'success',
        text: `Applicant status updated to "${newStatus}"!`
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: 'Failed to update applicant status.'
      });
    }
  };

  const handleOpenEditModal = () => {
    setEditFormData({
      name: rawProject.name || '',
      tagline: rawProject.tagline || '',
      description: rawProject.description || '',
      category: rawProject.category || rawProject.track || 'General',
      status: rawProject.status || 'Planning',
      teamSize: rawProject.teamSize || 4,
      startDate: rawProject.startDate || rawProject.start_date || '',
      endDate: rawProject.endDate || rawProject.end_date || '',
      estimatedHours: rawProject.estimatedHours || '',
      progress: rawProject.progress || 0,
      skills: Array.isArray(rawProject.requiredSkills)
        ? rawProject.requiredSkills.map((s, idx) => typeof s === 'string' ? { id: idx, name: s, level: 'Intermediate', importance: 'High' } : { id: idx, ...s })
        : []
    });
    setIsEditModalOpen(true);
  };

  const handleAddEditSkill = () => {
    if (newEditSkill.name.trim()) {
      setEditFormData(prev => ({
        ...prev,
        skills: [...prev.skills, { ...newEditSkill, id: Date.now() }]
      }));
      setNewEditSkill({ name: '', level: 'Intermediate', importance: 'High' });
    }
  };

  const handleRemoveEditSkill = (skillId) => {
    setEditFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s.id !== skillId)
    }));
  };

  const handleSaveProjectEdit = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) {
      alert('Project name cannot be empty.');
      return;
    }

    setIsSavingEdit(true);
    try {
      const updatedPayload = {
        ...rawProject,
        name: editFormData.name.trim(),
        tagline: editFormData.tagline.trim() || editFormData.description.slice(0, 80),
        description: editFormData.description,
        category: editFormData.category,
        track: editFormData.category,
        status: editFormData.status,
        teamSize: parseInt(editFormData.teamSize) || 4,
        startDate: editFormData.startDate,
        endDate: editFormData.endDate,
        estimatedHours: editFormData.estimatedHours ? parseInt(editFormData.estimatedHours) : null,
        progress: parseInt(editFormData.progress) || 0,
        requiredSkillsDetails: editFormData.skills,
        requiredSkills: editFormData.skills.map(s => s.name)
      };

      await api.updateProject(rawProject.id, updatedPayload);
      await loadData();
      setIsEditModalOpen(false);
      setFeedback({
        type: 'success',
        text: 'Project updated successfully!'
      });
      setTimeout(() => setFeedback(null), 4000);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: 'Failed to update project. Please try again.'
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Alert */}
      {feedback && (
        <div className={`p-4 rounded-xl flex items-center justify-between border animate-fade-in ${
          feedback.type === 'success' 
            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          <div className="flex items-center space-x-3">
            {feedback.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span className="text-sm font-medium">{feedback.text}</span>
          </div>
          <button onClick={() => setFeedback(null)} className="text-sm opacity-70 hover:opacity-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to="/projects"
            className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors text-white"
            title="Back to All Projects"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center flex-wrap gap-2.5">
              <h1 className="text-3xl font-bold">{project.name}</h1>
              {isOwner ? (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30">
                  Project Lead
                </span>
              ) : isMember ? (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Team Member
                </span>
              ) : userApplication ? (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                  userApplication.status === 'Accepted'
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : userApplication.status === 'Rejected'
                      ? 'bg-red-500/20 text-red-400 border-red-500/30'
                      : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                }`}>
                  Application {userApplication.status}
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  {openSpots > 0 ? `${openSpots} spots open` : 'Team full'}
                </span>
              )}
            </div>
            <p className="text-sm text-dark-300 mt-1">
              Led by <strong className="text-white">{project.creator || 'Admin'}</strong> • {project.creatorEmail ? project.creatorEmail : 'SkillMesh Organization'}
            </p>
          </div>
        </div>

        {/* Header Actions */}
        <div className="flex items-center flex-wrap gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
            {project.status}
          </span>

          {/* If user is not member and not applied, show Apply Now */}
          {!isMember && !userApplication && (
            <button 
              onClick={handleOpenApplyModal}
              className="btn-primary flex items-center space-x-2 text-sm shadow-lg"
            >
              <Send className="h-4 w-4" />
              <span>Apply to Join Team</span>
            </button>
          )}

          {/* If user already applied, show application status card */}
          {!isMember && userApplication && (
            <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-dark-800 border border-dark-700 text-xs">
              <Clock className="h-4 w-4 text-amber-400" />
              <span>Applied on {new Date(userApplication.appliedAt).toLocaleDateString()}</span>
            </div>
          )}

          {isOwner && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleOpenEditModal}
                className="btn-secondary flex items-center text-sm shadow-md"
              >
                <Edit3 className="h-4 w-4 mr-1.5 text-primary-400" />
                <span>Edit Project</span>
              </button>
              <Link to={`/match?project=${project.id}`} className="btn-secondary flex items-center text-sm">
                <Sparkles className="h-4 w-4 mr-1.5 text-primary-400" />
                Matching Engine
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Skill Fit Banner for Logged-In Normal User */}
      {currentUser && !isMember && (
        <div className={`p-4 rounded-xl border flex flex-col md:flex-row md:items-center justify-between gap-4 ${
          skillMatch.matchPercentage >= 75
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
            : skillMatch.matchPercentage >= 40
              ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
              : 'bg-dark-800/80 border-dark-700 text-dark-300'
        }`}>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <Target className="h-5 w-5" />
              <span className="font-bold text-base text-white">
                Your Skill Fit: {skillMatch.matchPercentage}% Match
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-black/30 font-medium">
                {skillMatch.matchCount} of {skillMatch.totalRequired} skills matched
              </span>
            </div>
            <p className="text-xs text-dark-300">
              {skillMatch.matchPercentage >= 75
                ? 'Great match! Your skill set aligns exceptionally well with what this project requires.'
                : skillMatch.matchPercentage >= 40
                  ? 'Good potential! You meet several key requirements for this project.'
                  : 'You have some skill gaps for this project, but you can still apply or build new skills.'}
            </p>
          </div>

          {!userApplication && (
            <button
              onClick={handleOpenApplyModal}
              className="btn-primary whitespace-nowrap text-xs py-2 px-4 self-start md:self-auto"
            >
              Apply With Your Skills →
            </button>
          )}
        </div>
      )}

      {/* Project Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card text-center p-4">
          <div className="text-2xl font-bold text-blue-400">{project.progress}%</div>
          <div className="text-xs text-dark-300 mt-1">Progress</div>
        </div>
        
        <div className="card text-center p-4">
          <div className="text-2xl font-bold text-purple-400">{currentTeamCount}/{teamSize}</div>
          <div className="text-xs text-dark-300 mt-1">
            Team ({openSpots > 0 ? `${openSpots} open spot${openSpots > 1 ? 's' : ''}` : 'Filled'})
          </div>
        </div>
        
        <div className="card text-center p-4">
          <div className="text-2xl font-bold text-green-400">{project.skillsList.length}</div>
          <div className="text-xs text-dark-300 mt-1">Required Skills</div>
        </div>
        
        <div className="card text-center p-4">
          <div className="text-2xl font-bold text-yellow-400">
            {project.endDate ? new Date(project.endDate).toLocaleDateString() : 'No Deadline'}
          </div>
          <div className="text-xs text-dark-300 mt-1">
            {project.estimatedHours ? `${project.estimatedHours}h Estimated Effort` : 'Target Deadline'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-dark-800">
          <nav className="flex space-x-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-400'
                    : 'border-transparent text-dark-300 hover:text-white hover:border-dark-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.highlight && (
                  <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="py-6">
          {/* TAB 1: OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Project Description</h3>
                <p className="text-dark-300 leading-relaxed text-sm">
                  {project.description || 'No detailed description provided.'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-dark-800">
                <div>
                  <h3 className="text-base font-semibold mb-3">Project Metadata</h3>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-dark-300">Creator:</span>
                      <span className="font-medium text-white">{project.creator || 'Admin'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-300">Contact Email:</span>
                      <span className="font-mono text-xs">{project.creatorEmail || 'admin@leanistic.com'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-300">Start Date:</span>
                      <span>{project.startDate ? new Date(project.startDate).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-dark-300">Target Completion:</span>
                      <span>{project.endDate ? new Date(project.endDate).toLocaleDateString() : 'Flexible / No fixed deadline'}</span>
                    </div>
                    {project.estimatedHours && (
                      <div className="flex justify-between">
                        <span className="text-dark-300">Estimated Effort:</span>
                        <span>{project.estimatedHours} hours</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-base font-semibold mb-3">Milestone Progress</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-dark-300">Execution Progress</span>
                      <span className="font-semibold">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-dark-800 rounded-full h-3">
                      <div 
                        className="bg-gradient-primary h-3 rounded-full transition-all duration-300"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-dark-400 pt-1">
                      <span>Status: {project.status}</span>
                      <span>Team spots: {currentTeamCount}/{teamSize} filled</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: REQUIRED SKILLS */}
          {activeTab === 'skills' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold">Needed Skills Breakdown</h3>
                  <p className="text-xs text-dark-300">
                    The skills, proficiency levels, and importance required for successful delivery.
                  </p>
                </div>
                {currentUser && (
                  <div className="text-xs bg-dark-800 px-3 py-1.5 rounded-lg border border-dark-700">
                    Your match: <strong className="text-emerald-400">{skillMatch.matchCount} of {skillMatch.totalRequired}</strong> skills
                  </div>
                )}
              </div>
              
              {project.skillsList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {project.skillsList.map((skill, index) => {
                    const skillName = skill.name || skill;
                    const isMatched = skillMatch.matchedSkills.some(s => s.toLowerCase() === skillName.toLowerCase());

                    return (
                      <div 
                        key={index} 
                        className={`card border p-4 transition-all ${
                          isMatched ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-dark-800'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-bold text-base flex items-center space-x-1.5">
                              {isMatched && <CheckCircle className="h-4 w-4 text-emerald-400" />}
                              <span>{skillName}</span>
                            </h4>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${getImportanceColor(skill.importance)}`}>
                            {skill.importance || 'High'}
                          </span>
                        </div>

                        <div className="text-xs text-dark-300 space-y-1 mb-3">
                          <div className="flex justify-between">
                            <span>Required Level:</span>
                            <span className="font-medium text-white">{skill.level || 'Intermediate'}</span>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-dark-800/80 flex items-center justify-between text-xs">
                          {isMatched ? (
                            <span className="text-emerald-400 font-medium flex items-center">
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              In Your Profile
                            </span>
                          ) : (
                            <span className="text-dark-400">Not in your profile</span>
                          )}
                          <Link 
                            to="/me"
                            className="text-primary-400 hover:text-primary-300 font-medium"
                          >
                            Update Profile →
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-dark-400 text-sm">No skills specified yet for this project.</p>
              )}
            </div>
          )}

          {/* TAB 3: TEAM */}
          {activeTab === 'team' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Project Team</h3>
                  <p className="text-xs text-dark-300">
                    Current collaborators assigned to this project ({currentTeamCount} of {teamSize} spots).
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {project.members.map((member) => (
                  <div key={member.id} className="card p-4 border border-dark-800">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {member.avatar}
                      </div>
                      <div className="flex-1 truncate">
                        <h4 className="font-semibold text-sm truncate">{member.name}</h4>
                        <p className="text-xs text-dark-300">{member.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {openSpots > 0 && (
                <div className="p-6 rounded-xl border-2 border-dashed border-dark-700 text-center space-y-3">
                  <div className="w-12 h-12 bg-dark-800 rounded-full flex items-center justify-center mx-auto text-primary-400">
                    <Users className="h-6 w-6" />
                  </div>
                  <h4 className="font-semibold text-base">This Project Has {openSpots} Open Spot{openSpots > 1 ? 's' : ''}</h4>
                  <p className="text-xs text-dark-300 max-w-md mx-auto">
                    {isOwner 
                      ? 'Review applications from the Applicants tab or invite talent directly.'
                      : 'You can apply with your skills to join this project team!'}
                  </p>
                  {!isMember && !userApplication && (
                    <button onClick={handleOpenApplyModal} className="btn-primary text-xs py-2 px-4 inline-flex items-center space-x-1.5">
                      <Send className="h-3.5 w-3.5" />
                      <span>Apply to Join Team</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: APPLICANTS (For Project Owner & Admin) */}
          {activeTab === 'applicants' && isOwner && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Incoming Applications</h3>
                  <p className="text-xs text-dark-300">
                    Review participants who applied to join your project team.
                  </p>
                </div>
                <span className="text-xs bg-primary-500/20 text-primary-400 px-2.5 py-1 rounded-full font-bold">
                  {projectApplications.length} Application{projectApplications.length !== 1 ? 's' : ''}
                </span>
              </div>

              {projectApplications.length > 0 ? (
                <div className="space-y-4">
                  {projectApplications.map((app) => {
                    const applicantSkills = app.skills || [];
                    const applicantMatch = calculateProjectSkillMatch(project.requiredSkills, applicantSkills);

                    return (
                      <div key={app.id} className="card p-5 border border-dark-800 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-sm">
                              {(app.userName || 'A').slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-white">{app.userName}</div>
                              <div className="text-xs text-dark-300">{app.userEmail}</div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-dark-400">
                              Applied {new Date(app.appliedAt).toLocaleDateString()}
                            </span>
                            <span className={`px-2.5 py-0.5 rounded text-xs font-bold border ${
                              app.status === 'Accepted'
                                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                : app.status === 'Rejected'
                                  ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                  : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                            }`}>
                              {app.status}
                            </span>
                          </div>
                        </div>

                        {/* Note / Pitch */}
                        {app.message && (
                          <div className="p-3 rounded-lg bg-dark-800/80 border border-dark-700/60 text-xs text-dark-200 leading-relaxed">
                            <span className="font-semibold text-white mr-1">Pitch:</span>
                            {app.message}
                          </div>
                        )}

                        {/* Skill Match Breakdown */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-dark-300">Skill Fit with this project:</span>
                            <span className="font-bold text-emerald-400">
                              {applicantMatch.matchPercentage}% ({applicantMatch.matchCount}/{applicantMatch.totalRequired} skills)
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {applicantSkills.map(sk => (
                              <span key={sk} className="px-2 py-0.5 rounded text-[11px] bg-dark-800 text-dark-300 border border-dark-700">
                                {sk}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Decision Actions for Pending Applicants */}
                        {app.status === 'Pending' && (
                          <div className="flex justify-end space-x-3 pt-3 border-t border-dark-800">
                            <button
                              onClick={() => handleUpdateApplicantStatus(app.userEmail, 'Rejected')}
                              className="btn-secondary text-xs py-1.5 px-3 text-red-400 hover:text-red-300 flex items-center space-x-1"
                            >
                              <UserX className="h-3.5 w-3.5" />
                              <span>Decline</span>
                            </button>
                            <button
                              onClick={() => handleUpdateApplicantStatus(app.userEmail, 'Accepted')}
                              className="btn-primary text-xs py-1.5 px-3 flex items-center space-x-1"
                            >
                              <UserCheck className="h-3.5 w-3.5" />
                              <span>Accept & Add to Team</span>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 rounded-xl border border-dashed border-dark-800 text-center text-dark-400 text-sm">
                  No applications received yet for this project.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Apply to Project Modal */}
      {isApplyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto border border-dark-700 bg-dark-900 text-white shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-dark-800">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center text-white">
                  <Send className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Apply to Join Project</h3>
                  <p className="text-xs text-dark-300">Submit your application to {project.creator || 'the project owner'}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsApplyModalOpen(false)}
                className="p-1 rounded-lg hover:bg-dark-800 text-dark-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleApplySubmit} className="space-y-4 py-4">
              {/* Project summary */}
              <div className="p-3 rounded-xl bg-dark-800/60 border border-dark-700 space-y-1">
                <div className="font-bold text-white text-sm">{project.name}</div>
                <div className="text-xs text-dark-300 line-clamp-2">{project.description}</div>
              </div>

              {/* Skill Match Breakdown */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-dark-300 font-medium">Your Skill Fit:</span>
                  <span className="font-bold text-emerald-400">
                    {skillMatch.matchPercentage}% ({skillMatch.matchCount} of {skillMatch.totalRequired} skills)
                  </span>
                </div>
                <div className="w-full bg-dark-800 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-gradient-primary transition-all duration-300"
                    style={{ width: `${skillMatch.matchPercentage}%` }}
                  />
                </div>
              </div>

              {/* Pitch note */}
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-dark-200">
                  Note to Project Lead:
                </label>
                <textarea
                  rows={4}
                  required
                  value={pitchMessage}
                  onChange={(e) => setPitchMessage(e.target.value)}
                  placeholder="Introduce yourself, your relevant background, and how you plan to contribute..."
                  className="input-field w-full text-sm resize-none dark"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-3 border-t border-dark-800">
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  className="btn-secondary text-sm"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary text-sm flex items-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Submit Application</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
          <div className={`w-full max-w-2xl my-8 rounded-2xl p-6 shadow-2xl border transition-all ${
            isDarkMode ? 'bg-dark-900 border-dark-700 text-white' : 'bg-white border-light-200 text-light-950'
          }`}>
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-700/40">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center">
                  <Edit3 className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Edit Project</h3>
                  <p className="text-xs text-gray-400">Update project details, deadlines, and needed skills</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="p-1 rounded-lg hover:bg-dark-800 text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSaveProjectEdit} className="space-y-6">
              {/* Basic Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Category
                  </label>
                  <select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  >
                    {PROJECT_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Tagline / Short Pitch
                  </label>
                  <input
                    type="text"
                    value={editFormData.tagline}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="Short 1-sentence summary"
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  >
                    <option value="Planning">Planning</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Target Team Size
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={editFormData.teamSize}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, teamSize: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Target Due Date
                  </label>
                  <input
                    type="date"
                    value={editFormData.endDate}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                    Progress (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editFormData.progress}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, progress: e.target.value }))}
                    className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                  Full Problem Statement & Description
                </label>
                <textarea
                  rows={4}
                  value={editFormData.description}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>

              {/* Needed Skills Editor */}
              <div className={`p-4 rounded-xl border ${
                isDarkMode ? 'bg-dark-800/80 border-dark-700' : 'bg-light-100 border-light-200'
              }`}>
                <h4 className="text-sm font-bold mb-2 flex items-center">
                  <Target className="h-4 w-4 mr-1.5 text-primary-400" />
                  Manage Needed Skills ({editFormData.skills.length})
                </h4>
                
                {/* Add new skill inline */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="Skill name (e.g. PyTorch, React)"
                    value={newEditSkill.name}
                    onChange={(e) => setNewEditSkill(prev => ({ ...prev, name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEditSkill();
                      }
                    }}
                    className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                  />
                  <select
                    value={newEditSkill.level}
                    onChange={(e) => setNewEditSkill(prev => ({ ...prev, level: e.target.value }))}
                    className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Expert">Expert</option>
                  </select>
                  <select
                    value={newEditSkill.importance}
                    onChange={(e) => setNewEditSkill(prev => ({ ...prev, importance: e.target.value }))}
                    className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
                
                <button
                  type="button"
                  onClick={handleAddEditSkill}
                  className="btn-secondary py-1.5 px-3 text-xs flex items-center space-x-1 mb-4"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Add Skill</span>
                </button>

                {/* List of currently assigned needed skills */}
                <div className="flex flex-wrap gap-2">
                  {editFormData.skills.map((s) => (
                    <span
                      key={s.id || s.name}
                      className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-xs border ${
                        isDarkMode ? 'bg-dark-700 border-dark-600 text-white' : 'bg-light-200 border-light-300 text-light-900'
                      }`}
                    >
                      <span className="font-semibold">{s.name}</span>
                      <span className="opacity-70 text-[10px]">({s.level || 'Intermediate'})</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEditSkill(s.id)}
                        className="text-red-400 hover:text-red-300 ml-1 p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {editFormData.skills.length === 0 && (
                    <span className="text-xs text-gray-400 italic">No skills listed yet.</span>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700/40">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSavingEdit}
                  className={`btn-secondary py-2 px-4 text-sm ${isDarkMode ? 'dark' : 'light'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit || !editFormData.name.trim()}
                  className="btn-primary py-2 px-5 text-sm flex items-center space-x-2"
                >
                  {isSavingEdit ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Saving Changes...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
